import { Coffee, MoonStar, UtensilsCrossed, Salad, Wine, ShoppingBag, Sandwich, Milk, Wheat, CakeSlice, Croissant } from "lucide-react";
import { useLang } from "@/App";
import { all, pick, seq } from "@/data/images";
import Hero from "@/components/Hero";
import Foto from "@/components/Foto";
import GaleriaGrid from "@/components/GaleriaGrid";
import { Reveal, Pretitulo, TituloSeccion, BtnPrimario } from "@/components/Reveal";

const ICONOS = { coffee: Coffee, moon: MoonStar, utensils: UtensilsCrossed, leaf: Salad, wine: Wine, "shopping-bag": ShoppingBag };

// Tetera (estilo lucide: trazo, currentColor) para "Distintos tés"
const Teapot = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 11h13v1a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6v-1z" />
    <path d="M4 12.5 1.5 11" />
    <path d="M17 12a3 3 0 0 1 3 3v1" />
    <path d="M8 11V9.5a3 3 0 0 1 6 0V11" />
    <path d="M11 6.5V4.5" />
  </svg>
);

// Íconos del desayuno, en el mismo orden que lista1
const ICONOS_DESAYUNO = [Coffee, Teapot, Sandwich, Milk, Wheat, CakeSlice, Croissant];

export default function Gastronomia() {
  const { t, L } = useLang();
  const p = t.gastronomia;

  return (
    <div data-testid="pagina-gastronomia">
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid="hero-gastronomia" />

      {/* 2. Presentación */}
      <section className="py-20 md:py-28" data-testid="g-presentacion">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[6fr_5fr] lg:gap-20">
          <Reveal>
            <Pretitulo center={false}>{p.presentacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.presentacion.titulo}</h2>
            <p className="mt-7 max-w-2xl text-[15.5px] leading-relaxed text-ink-soft">{p.presentacion.cuerpo}</p>
          </Reveal>
          <Reveal delay={0.12} className="img-frame aspect-[4/3.4] w-full">
            <Foto src={pick(p.presentacion.foto[0], 0) || all(p.presentacion.foto[0])[0]} alt="" className="h-full w-full" testid="g-presentacion-foto" />
          </Reveal>
        </div>
      </section>

      {/* 3. Lo que ofrecemos en la mesa */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="g-mesa">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={p.mesa.pre} titulo={p.mesa.titulo} />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
            {p.mesa.items.map((item, i) => {
              const Icono = ICONOS[p.mesa.iconos[i]] || UtensilsCrossed;
              return (
                <Reveal key={item} delay={i * 0.07} className="flex items-start gap-5 border border-lineas bg-sand p-7" testid={`g-mesa-item-${i}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center text-terra">
                    <Icono size={26} strokeWidth={1.3} />
                  </span>
                  <p className="pt-2 font-serif text-xl leading-snug text-ink">{item}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Almuerzos y cenas */}
      <section className="py-20 md:py-28" data-testid="g-almuerzos">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[6fr_5fr] lg:gap-20">
          <Reveal className="img-frame aspect-[4/3.2] w-full">
            <Foto src={pick(p.almuerzos.foto[0], 0) || all(p.almuerzos.foto[0])[0]} alt="" className="h-full w-full" testid="g-almuerzos-foto" />
          </Reveal>
          <Reveal delay={0.12}>
            <Pretitulo center={false}>{p.almuerzos.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.almuerzos.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15px] leading-relaxed text-ink-soft">{p.almuerzos.texto}</p>
            <ul className="mt-7 space-y-2.5">
              {p.almuerzos.platos.map((pl) => (
                <li key={pl} className="flex items-baseline gap-3 text-[15px] text-ink-soft">
                  <svg viewBox="0 0 512 512" className="mt-1 h-4 w-4 shrink-0 fill-terra" aria-hidden="true">
                    <path d="M428.3 3c11.6-6.4 26.2-2.3 32.6 9.3l4.8 8.7c19.3 34.7 19.8 75.7 3.4 110c26.7 28.6 42.9 66.9 42.9 109c0 18.5-3.1 36.3-8.9 52.8c-6.1 17.3-28.5 16.3-36.8-.1l-11.7-23.4c-4.1-8.1-12.4-13.3-21.5-13.3H360c-13.3 0-24-10.7-24-24v-80c0-13.3-10.7-24-24-24h-17.1c-21.3 0-30-23.9-10.8-32.9C304.7 85.4 327.7 80 352 80c28.3 0 54.8 7.3 77.8 20.2c5.5-18.2 3.7-38.4-6-55.8l-4.8-8.7c-6.4-11.6-2.3-26.2 9.3-32.6zM171.2 345.5L264 160h40v80c0 26.5 21.5 48 48 48h76.2l23.9 47.8C372.3 443.9 244.3 512 103.2 512H44.4C19.9 512 0 492.1 0 467.6c0-20.8 14.5-38.8 34.8-43.3l49.8-11.1c37.6-8.4 69.5-33.2 86.7-67.7z" />
                  </svg>
                  {pl}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* 5. Desayuno — bloque de alto contraste */}
      <section className="bg-ink py-20 md:py-28" data-testid="g-desayuno">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_6fr] lg:gap-20">
          <Reveal>
            <Pretitulo claro center={false}>{p.desayuno.pre}</Pretitulo>
            {/* Imagen solo en móvil: aparece justo debajo del pretítulo "CADA MAÑANA" */}
            <div className="img-frame mt-6 aspect-[4/3] w-full lg:hidden">
              <Foto src={pick(p.desayuno.foto[0], 0) || all(p.desayuno.foto[0])[0]} alt="" className="h-full w-full" testid="g-desayuno-foto-movil" />
            </div>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-sand md:text-5xl">{p.desayuno.titulo}</h2>
            <p className="mt-6 text-[15px] text-sand/75">{p.desayuno.texto}</p>
            <ul className="mt-7 space-y-2.5">
              {p.desayuno.lista1.map((li, i) => {
                const Ico = ICONOS_DESAYUNO[i] || Coffee;
                return (
                  <li key={li} className="flex items-baseline gap-3 text-[15px] text-sand/85">
                    <Ico className="h-4 w-4 shrink-0 text-terra" strokeWidth={1.8} aria-hidden="true" /> {li}
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-sand/60">{p.desayuno.lista2}</p>
          </Reveal>
          <Reveal delay={0.12} className="img-frame hidden aspect-[4/3] w-full lg:block">
            <Foto src={pick(p.desayuno.foto[0], 0) || all(p.desayuno.foto[0])[0]} alt="" className="h-full w-full" testid="g-desayuno-foto" />
          </Reveal>
        </div>
      </section>

      {/* 6. Parrilla / Cocina y sala comedor */}
      <section className="py-20 md:py-28" data-testid="g-bloques">
        <div className="mx-auto grid max-w-[1300px] gap-10 px-6 md:px-10 lg:grid-cols-2 lg:gap-14">
          {[p.bloques.parrilla, p.bloques.cocina].map((b, i) => (
            <Reveal key={b.titulo} delay={i * 0.1} className="flex flex-col" testid={`g-bloque-${i}`}>
              <div className="img-frame aspect-[4/2.8] w-full">
                <Foto src={pick(b.foto[0], 0) || all(b.foto[0])[0]} alt="" className="h-full w-full" testid={`g-bloque-${i}-foto`} />
              </div>
              <div className="mt-6"><Pretitulo center={false}>{b.pre}</Pretitulo></div>
              <h3 className="mt-3 font-serif text-2xl font-light text-ink md:text-3xl">{b.titulo}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">{b.texto}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 7. Galería */}
      <section className="pb-20 md:pb-28" data-testid="g-galeria">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10">
          <TituloSeccion pre={p.galeria.pre} titulo={p.galeria.titulo} />
          <GaleriaGastronomia config={p.galeria} />
          <div className="mt-10 text-center">
            <BtnPrimario href="fotos/gastronomia" testid="g-galeria-ver-btn">VER GALERÍA</BtnPrimario>
          </div>
        </div>
      </section>
    </div>
  );
}

function GaleriaGastronomia({ config }) {
  const hero = pick(config.usar, 0) || all(config.usar)[0];
  const excl = hero ? [hero.split("/").pop()] : [];
  const fotos = [hero, ...seq(config.carpeta, 10, excl).filter((f) => f !== hero)].filter(Boolean).slice(0, 10);
  return <GaleriaGrid fotos={fotos} columnas={5} testid="g-galeria-grid" />;
}